import { z } from 'zod';
import { jsonObject, uuid } from './common.js';
import { listQueryInput, paginated } from './list.js';
import { personaSchema } from './document.js';
/**
 * Persona CRUD op DTOs (F16, `/personas`). The entity (`personaSchema`) lives
 * in `./document.ts` — personas are a document-generation input (F1/F-Estep
 * step ⑤ prefill), so `documents.ts`/`document.ts` own the shape; this file
 * owns the `/personas` page's own WS ops, mirroring the `document.ts` +
 * `document.dto.ts` split.
 */
export const personaCreateInput = z.object({
    name: z.string().trim().min(1, 'errors:persona.name.required').max(160, 'errors:persona.name.tooLong'),
    description: z.string().trim().max(2000, 'errors:persona.description.tooLong').optional(),
    /** Voice/style directives applied at generation time (art-style/voice/narration fields, F16 AC). */
    styleJsonb: jsonObject,
    isPublic: z.boolean().default(false),
});
export const personaCreateResult = personaSchema;
/**
 * `persona.update` — ownership enforced server-side (owner or admin
 * staffOverride only, §15); a seeded/public catalog persona (`ownerId: null`)
 * is never editable by a non-admin. Existing documents keep their generation
 * SNAPSHOT regardless of later persona edits (F16 AC) — this op only changes
 * the persona row, never rewrites past `document_versions`.
 */
export const personaUpdateInput = z.object({
    personaId: uuid,
    name: z.string().trim().min(1, 'errors:persona.name.required').max(160, 'errors:persona.name.tooLong').optional(),
    description: z.string().trim().max(2000, 'errors:persona.description.tooLong').nullable().optional(),
    styleJsonb: jsonObject.optional(),
    isPublic: z.boolean().optional(),
});
export const personaUpdateResult = personaSchema;
/**
 * `persona.delete` — F16 AC: deleting a persona used by existing documents
 * leaves those documents' SNAPSHOT untouched (`documents.personaId` is a
 * plain reference with no cascade); the handler warns the caller
 * client-side before calling this (asset-named confirm, E12 §16.1), this op
 * itself does the hard delete (no soft-delete column on `personas`, matching
 * `packages/db/src/schema/documents.ts`'s existing shape — additive-only
 * scope here does not retrofit that).
 */
export const personaDeleteInput = z.object({ personaId: uuid });
export const personaDeleteResult = z.object({ personaId: uuid, deleted: z.boolean() });
export const personaSortColumns = ['createdAt', 'updatedAt', 'name'];
/**
 * `persona.list` — `scope: 'mine'` (default) = the caller's own + the seeded
 * public catalog (the `/personas` gallery); `scope: 'public'` = the public
 * catalog only (F16 AC: "importing a public persona from discovery into your
 * own set" picker). Server enforces the scope — never a client-supplied
 * `ownerId` filter (ownership-only reads, platform.md).
 */
export const personaListQuery = listQueryInput(personaSortColumns, {
    defaultSort: 'createdAt',
}).extend({
    scope: z.enum(['mine', 'public']).default('mine'),
});
export const personaListResult = paginated(personaSchema);
